// Global Instructions Rule Applied!
//
// Agent state card — the pills→cards redesign (owner, 2026-08-25; refined
// 2026-08-25: dates live on the PILL, not the card body; approved deviation,
// see docs/FRONTEND_DESIGN_PATTERNS.md): the full narrative sits inline on a
// compact card, a visual sibling of AgentSignalCard (same article treatment,
// 4px colored left border — by condition-rank band for persistent states,
// by kind for transitory/ask). The condition pill carries the last-evidence
// date, holds the full date detail in its hover tooltip, and IS the
// condition selector (click → condition menu) — no dropdown in the card
// body. Remaining actions: Edit summary / Dismiss / Create task.
// Agent-written content only — never reuse for human-authored data.
import React from 'react'
import { Button, Dropdown, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import {
  conditionBorderColor,
  conditionColor,
  kindBorderColor,
  kindColor
} from './stateColors.ts'
import type { CompanyStateCondition } from '../../../state_manager/data_models/internal_agents_model.ts'

/** Presentation-shape a strip wrapper maps its rows onto (CompanyState /
 *  DealState both fit — the row-key families differ, the content does not). */
export interface StateItem {
  /** null = persistent placeholder (no row yet — nothing to edit or task). */
  id: string | null
  stateKey: string
  label: string
  kind: string // 'persistent' | 'transitory' | 'ask'
  conditionKey: string | null
  conditionLabel: string | null
  conditionRank: number | null
  summary: string | null
  status: string | null
  startedAt: string | null
  lastConfirmedAt: string | null
  lastEvidenceAt: string | null
  lastCheckedAt: string | null
  expectedEndAt: string | null
  expectedEndBasis: string | null
  userSet: boolean
  /** The conditions catalogue as the state models validate it (shared by all four subjects). */
  conditions: CompanyStateCondition[]
  taskId: string | null
  taskSubject: string | null
  taskCompleted: boolean | null
  taskDueBy: string | null
}

interface StateCardProps {
  state: StateItem
  onSetCondition?: (state: StateItem, conditionKey: string) => void
  onEditSummary?: (state: StateItem) => void
  onDismiss?: (state: StateItem) => void
  /** Opens the pre-filled task form. Human-triggered by design. */
  onCreateTask?: (state: StateItem) => void
}

export const StateCard: React.FC<StateCardProps> = ({
  state,
  onSetCondition,
  onEditSummary,
  onDismiss,
  onCreateTask
}) => {
  const isPersistent = state.kind === 'persistent'
  const borderColor = isPersistent
    ? conditionBorderColor(state.conditionRank)
    : kindBorderColor(state.kind)

  // A3b: evidence date is the honest currency signal — it rides ON the pill.
  // Pre-A3b rows fall back to the legacy confirmed (run) date.
  const pillDate = state.lastEvidenceAt ?? state.lastConfirmedAt
  // Full date detail lives in the pill's hover tooltip (owner, 2026-08-25).
  const dateDetail = (
    <div className='text-[11.5px]'>
      <div>{state.startedAt ? `since ${dayjs(state.startedAt).format('MM/DD/YYYY')}` : 'not set yet'}</div>
      {state.lastEvidenceAt ? (
        <div>last evidence {dayjs(state.lastEvidenceAt).format('MM/DD/YYYY')}</div>
      ) : state.lastConfirmedAt ? (
        <div>confirmed {dayjs(state.lastConfirmedAt).format('MM/DD/YYYY')}</div>
      ) : null}
      {state.lastCheckedAt ? <div>checked {dayjs(state.lastCheckedAt).format('MM/DD/YYYY')}</div> : null}
      {state.expectedEndAt ? (
        <div>
          expected end {dayjs(state.expectedEndAt).format('MM/DD/YYYY')}
          {state.expectedEndBasis ? ` — “${state.expectedEndBasis}”` : ''}
        </div>
      ) : null}
      {state.userSet ? <div>user-set — authoritative until newer evidence</div> : null}
      {isPersistent && onSetCondition ? (
        <div className='opacity-70'>click to change the condition</div>
      ) : null}
    </div>
  )

  const pill = (
    <Tooltip title={dateDetail} mouseEnterDelay={0.3}>
      <Tag
        color={isPersistent ? conditionColor(state.conditionRank) : kindColor(state.kind)}
        className={`m-0 px-2 py-0 text-[11.5px] select-none${
          isPersistent && onSetCondition ? ' cursor-pointer' : ''
        }`}
      >
        {isPersistent ? (state.conditionLabel ?? 'Unknown') : state.kind}
        {pillDate ? (
          <span className='opacity-60 ml-1'>· {dayjs(pillDate).format('MM/DD/YY')}</span>
        ) : null}
      </Tag>
    </Tooltip>
  )

  return (
    <article
      data-component='StateCard'
      className='bg-white rounded-lg border border-gray-100 px-3.5 py-2.5 flex flex-col'
      style={{ borderLeftWidth: 4, borderLeftColor: borderColor }}
    >
      <div className='flex items-center gap-2 flex-wrap'>
        <span className='text-sm font-semibold'>{state.label}</span>
        {/* The pill is the condition selector for persistent states — click
            opens the condition menu; the old always-visible Select is gone
            (owner, 2026-08-25). */}
        {isPersistent && onSetCondition ? (
          <Dropdown
            trigger={['click']}
            menu={{
              items: state.conditions.map((c) => ({
                key: c.condition_key,
                label: c.label
              })),
              selectedKeys: state.conditionKey ? [state.conditionKey] : [],
              onClick: ({ key }) => {
                if (key !== state.conditionKey) onSetCondition(state, key)
              }
            }}
          >
            <span className='inline-flex'>{pill}</span>
          </Dropdown>
        ) : (
          pill
        )}
        {state.userSet ? (
          <span className='text-gray-400 text-[11px]'>user-set</span>
        ) : null}
      </div>

      {state.summary ? (
        <p className='m-0 mt-1 text-[12.5px] text-gray-700 whitespace-pre-line'>
          {state.summary}
        </p>
      ) : (
        <p className='m-0 mt-1 text-[12.5px] text-gray-400'>No narrative yet.</p>
      )}

      <div className='flex items-center gap-2.5 mt-auto pt-2 flex-wrap'>
        {state.id && onEditSummary ? (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-[11.5px]'
            onClick={() => onEditSummary(state)}
          >
            Edit summary
          </button>
        ) : null}
        {state.id && !isPersistent && onDismiss ? (
          <button
            type='button'
            className='cursor-pointer text-red-600 hover:text-red-700 hover:underline bg-transparent border-0 p-0 text-[11.5px]'
            onClick={() => onDismiss(state)}
          >
            Dismiss
          </button>
        ) : null}
        {/* Raising a task is the one action that turns a state into work, and
            it is always a person's decision. Hidden once this chain already
            carries one; the chip mirrors AgentSignalCard's. */}
        {state.id && !state.taskId && onCreateTask ? (
          <Button size='small' onClick={() => onCreateTask(state)}>
            Create task
          </Button>
        ) : null}
        {state.taskId ? (
          <span
            className='text-[11.5px] rounded px-1.5 py-0.5 flex items-center gap-1'
            style={{ color: '#3F6212', background: '#F2F7E9' }}
            title={state.taskSubject ?? undefined}
          >
            <span className='font-semibold uppercase tracking-wide text-[10px]'>
              {state.taskCompleted ? 'Task done' : 'Task'}
            </span>
            <span className='max-w-[180px] truncate'>{state.taskSubject}</span>
            {state.taskDueBy ? (
              <span className='tabular-nums text-gray-500'>
                due {dayjs(state.taskDueBy).format('MM/DD/YYYY')}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </article>
  )
}
